/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2023, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import {
  Dropdown,
  TextInput,
  PasswordInput,
  InlineNotification,
  Tooltip,
  Button,
  CodeSnippet
} from 'carbon-components-react';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { useMount } from 'react-use';

import { selectDraftEnvironmentId } from '@ibm-watson/shared/modules/ia/store/environments/selectors';
import {
  addMessage,
  ToastStatus
} from '@ibm-watson/shared/modules/ia/store/messages/actions';
import { useAppDispatch, useAppSelector } from '@ibm-watson/shared/store';
import { useFormHelpers } from '../../../hooks/useFormHelpers';
import { selectCatalogItemById } from '../../../store/catalog/selectors';
import {
  createIntegration,
  updateIntegration
} from '../../../store/channels/actions';
import {
  selectDraftIntegration,
  selectIsIntegrationDirty
} from '../../../store/channels/selectors';
import { ChannelContentProps } from '../../../util/types';
import {
  AUTHENTICATION_METHODS,
  BODY,
  CODE,
  BASIC_AUTH_HEADER,
  DEFAULT_OAUTH_FLOW_SCHEMA,
  OAUTH2_AUTHORIZATION_FLOW_REDIRECT_URI,
  OAUTH2_FLOW_TYPES,
  OAUTH2_FLOWS_REQUIRED_FIELDS,
  SUPPORTED_OAUTH2_FLOWS,
  STATE,
  TOAST_NOTIFICATION_TIMEOUT_SUCCESS_OAUTH_CODE,
  VALUE,
  CLIENT_SECRET,
  USERNAME,
  defaultOAuth2Secrets,
  defaultOAuth2CustomFlowSchema,
  defaultOAuth2CustomFlowSecretSchema,
  BEARER
} from '../constants';
import {
  getGrantFriendlyName,
  getFlowTypeName,
  getScopes,
  isCustomFlow,
  getAbsoluteURL
} from '../helpers';
import { FIELDS } from '../schema';
import { AuthenticationStepMessages as messages } from './messages';

interface AuthenticationContentOAuthProps extends ChannelContentProps {
  catalogItemId: string;
}

/**
 * AuthenticationContentOAuth Component
 *
 * This component renders OAuth2 UI components when the selected auth type is OAuth2
 * This renders "Grant type" dropdown which basically supports 4 grant types viz.
 * authorization code, client credentials, password & custom flow starts with "x-".
 * Based on selected grant type (aka flow type), it renders a respective forms
 */
export const AuthenticationContentOAuth = ({
  assistantId,
  catalogItemId,
  environmentId,
  isQuickSetup,
  readOnly
}: AuthenticationContentOAuthProps) => {
  const dispatch = useAppDispatch();
  const { formatMessage: t } = useIntl();

  const formMethods = useFormContext();
  const { trigger } = formMethods;
  const { getFieldValue, updateFieldValue, registerInputField } =
    useFormHelpers(formMethods);

  const authenticationName = getFieldValue(FIELDS.AUTHENTICATION_DISPLAY_NAME);
  const isDirty = useAppSelector(selectIsIntegrationDirty);

  const { search, pathname } = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(search), [search]);
  const authCode = useMemo(() => queryParams.get(CODE) ?? '', [queryParams]);
  const {
    authClientId = '',
    authClientSecret = '',
    authClientType = '',
    authHeaderPrefix = '',
    authName = ''
  } = JSON.parse(queryParams.get(STATE) ?? '{}');
  const generatedServerUrl = getFieldValue(FIELDS.GENERATED_SERVER_URL);

  const draftEnvironmentId = useAppSelector(selectDraftEnvironmentId);
  const draftIntegration = useAppSelector(selectDraftIntegration);
  const draftCatalogItem = useAppSelector(state =>
    selectCatalogItemById(state, catalogItemId)
  );

  const headerPrefixChangeState = useState<boolean>(false);

  // We have to use a "fake useState" here, because we have a
  // variating number of states to keep track of. React throws an
  // error when it notices that a different number OR sequence of
  // hooks are traced between each component render.
  const maxOAuthStates = Object.values(OAUTH2_FLOWS_REQUIRED_FIELDS).reduce(
    (maxItems: number, fields: string[]) =>
      fields.length > maxItems ? fields.length : maxItems,
    0
  );
  const oauth2FieldChangeStates = Array.from({ length: maxOAuthStates }, () => {
    let state = false;
    const changeState = (newState: boolean) => {
      state = newState;
    };
    return [state, changeState];
  });

  const securitySchemes = _.get(
    draftCatalogItem,
    'specification.body.components.securitySchemes',
    {}
  );

  // authName is nothing but a selected securietyScheme name from openAPI spec
  // For store it's a name param under authentication
  // In the case of authCode flow, when flow comesback from redirection, this will get back the selected authName
  const selectedAuth = (): string => {
    if (authCode) {
      return authName;
    } else if (authenticationName) {
      return authenticationName;
    } else {
      return AUTHENTICATION_METHODS.OAUTH2;
    }
  };

  // As securitySchemes can have any custom name, we need to select the one which has flows in it for OAuth2.
  // Below will get back flow array
  let catalogItemFlows = (
    Object.values(securitySchemes) as { flows?: unknown }[]
  )
    ?.filter(obj => obj.flows)
    ?.map(obj => obj.flows)[0];

  // If authenticationName is defined, prefer its flows
  if (authenticationName) {
    catalogItemFlows = _.get(securitySchemes, `${selectedAuth()}.flows`, {});
  }

  // Business logic for flows:
  // 1. Convert flows in snakecase from camelCase as store needs it in that format.
  // 2. We only support three flow types for OAauth2 & custom flow types which start with x-
  // 3. This also sort the flows alphabatically
  const flows = Object.keys(catalogItemFlows)
    ?.map(flow => getFlowTypeName(flow))
    ?.filter(
      (flow: OAUTH2_FLOW_TYPES) =>
        SUPPORTED_OAUTH2_FLOWS.includes(flow) || isCustomFlow(flow)
    )
    ?.sort();

  // Creates flow type / Grant type dropdown
  const flowsDropdownItems = flows?.map((flow, index) => ({
    text: isCustomFlow(flow)
      ? `${t(messages.custom)} ${flow.replace('x-', '')}`
      : getGrantFriendlyName[flow],
    value: flow,
    id: `${_.kebabCase(flow)}-${index}`
  }));

  // Creates client Auth type dropdown
  const clientAuthTypeDropdownItems = [
    {
      text: t(messages.basicAuthHeader),
      value: BASIC_AUTH_HEADER,
      id: 'option-basicAuthHeader-1'
    },
    {
      text: t(messages.body),
      value: BODY,
      id: 'option-body-1'
    }
  ];

  const preferredFlow = getFieldValue(FIELDS.OAUTH2.PREFERRED_FLOW);

  // Get flow type (grant type) from selected dropdown or get the first one
  // In case of oauth redirect, select flow as authorization_code
  const selectedFlowType = authCode
    ? OAUTH2_FLOW_TYPES.AUTHORIZATION_CODE
    : flows.includes(preferredFlow)
    ? preferredFlow
    : flows?.[0];

  const customFlow = isCustomFlow(selectedFlowType);

  const {
    authorizationUrl = '',
    tokenUrl = '',
    refreshUrl = '',
    grantType = '',
    scopes = {}
  } = catalogItemFlows[
    customFlow ? selectedFlowType : _.camelCase(selectedFlowType)
  ] || DEFAULT_OAUTH_FLOW_SCHEMA;

  const clientId =
    getFieldValue(FIELDS.OAUTH2.CLIENT_ID(selectedFlowType)) || '';
  const authorizationCode =
    getFieldValue(FIELDS.OAUTH2.AUTHORIZATION_CODE) || '';
  const clientAuthType =
    (customFlow
      ? getFieldValue(FIELDS.OAUTH2.CUSTOM.AUTH_TYPE(selectedFlowType))
      : getFieldValue(FIELDS.OAUTH2.CLIENT_AUTH_TYPE(selectedFlowType))) ||
    clientAuthTypeDropdownItems[0].value;
  const headerPrefix =
    customFlow && isQuickSetup
      ? BEARER
      : getFieldValue(FIELDS.OAUTH2.CUSTOM.HEADER_PREFIX(selectedFlowType));

  // Sets the values from openAPI spec to the OAuth part of payload of /tooling_integrations/definition/id call
  useEffect(() => {
    updateFieldValue(FIELDS.OAUTH2.PREFERRED_FLOW, selectedFlowType);
    updateFieldValue(FIELDS.OAUTH2.AUTHORIZATION_URL, authorizationUrl);
    if (!customFlow) {
      updateFieldValue(FIELDS.OAUTH2.TOKEN_URL(selectedFlowType), tokenUrl);
      updateFieldValue(FIELDS.OAUTH2.REFRESH_URL(selectedFlowType), refreshUrl);

      if (!_.isEmpty(scopes)) {
        updateFieldValue(FIELDS.OAUTH2.SCOPES(selectedFlowType), scopes);
      }
    }
  }, [
    authorizationUrl,
    customFlow,
    refreshUrl,
    scopes,
    selectedFlowType,
    tokenUrl,
    updateFieldValue
  ]);

  // This useEffect is used to set the field values coming back from redirect uri state for auth_code flow type
  // Since we create integration on click of "access grant" button to have integration_id so that when it redirects after
  // Successful redirect from third party auth client like Github etc, it sends us back the client_id and auth_code.
  // After coming back, we treat that integration as a non-quick setup now so we need to add {type: 'value'} back to the secret fields to save it further
  useEffect(() => {
    // To avoid the race condition with updateDraftIntegrationSettings we have added this timeout here, which re-adds secrets back
    // coming from the successful redirect.
    const triggerTimeout = setTimeout(() => {
      if (
        authCode &&
        selectedFlowType === OAUTH2_FLOW_TYPES.AUTHORIZATION_CODE
      ) {
        // sets a default secrets object for auth code flow
        updateFieldValue(
          FIELDS.OAUTH2.FLOWS_FLOW(selectedFlowType),
          defaultOAuth2Secrets()[selectedFlowType]
        );
        // Update PREFERRED_FLOW to AUTHORIZATION_CODE
        updateFieldValue(
          FIELDS.OAUTH2.PREFERRED_FLOW,
          OAUTH2_FLOW_TYPES.AUTHORIZATION_CODE
        );
        // update AUTHENTICATION_METHOD to OAUTH2
        updateFieldValue(
          FIELDS.AUTHENTICATION_METHOD,
          AUTHENTICATION_METHODS.OAUTH2
        );
        // Below two actually updates secrets - AUTHORIZATION_CODE & CLIENT_ID coming back after the successful redirect i.e when auth code found
        updateFieldValue(FIELDS.OAUTH2.AUTHORIZATION_CODE, authCode);
        updateFieldValue(
          FIELDS.OAUTH2.CLIENT_ID(selectedFlowType),
          authClientId
        );
      }
      return () => {
        clearTimeout(triggerTimeout);
      };
    }, 1200);
  }, [
    authClientId,
    authClientSecret,
    authClientType,
    authCode,
    authHeaderPrefix,
    selectedFlowType,
    updateFieldValue
  ]);

  // Check if it's a redirect, and see if it has a code then show toast notification
  useEffect(() => {
    if (
      search &&
      authCode &&
      selectedFlowType === OAUTH2_FLOW_TYPES.AUTHORIZATION_CODE
    ) {
      dispatch(
        addMessage({
          message: t(messages.authCodeFound),
          status: ToastStatus.SUCCESS,
          timeout: TOAST_NOTIFICATION_TIMEOUT_SUCCESS_OAUTH_CODE
        })
      );
    }
  }, [authCode, dispatch, search, selectedFlowType, t]);

  /**
   * Build the redirect URI to be used in an OAuth flow.
   *
   * The redirect URI is based on the current window location, but without any
   * query parameters. If there is a query param, the current query string
   * is included in the redirect URI.
   *
   * @returns {string} The redirect URI encoded as a string.
   */
  const redirectURI = useMemo(() => {
    // We are using baseURI instead of window.location.origin here is because of the premium slot path is being used for Tooling
    // Ex. document.baseURI is https://us-south.assistant.dev.watson.cloud.ibm.com/dev003/
    // Whereas window.location.origin is https://us-south.assistant.dev.watson.cloud.ibm.com
    const baseUrl = document.baseURI.replace(/\/$/, '');
    return encodeURIComponent(baseUrl + pathname);
  }, [pathname]);

  const handleAuthorizationCodeFlow = async () => {
    // Construct a redirect state object that includes information about the request
    const redirectState = encodeURIComponent(
      JSON.stringify({
        redirectURI,
        catalogItemId: draftCatalogItem?.catalog_item_id,
        environmentId: draftEnvironmentId,
        authClientId: clientId,
        authName: selectedAuth()
      })
    );

    // AuthorizationUrl needs to be absolute in order to successfully redirecting it to auth code provider
    const absoluteAuthorizationUrl = getAbsoluteURL(
      authorizationUrl,
      generatedServerUrl
    );

    // Construct the authorization URL by appending the query strings to the authorization endpoint URL
    // authorizationUrl is from the uploaded openAPI spec file
    // Support absoluteAuthorizationUrl containing it's own query params
    const paramDelimiter = absoluteAuthorizationUrl.includes('?') ? '&' : '?';
    const buildAuthURL = `${absoluteAuthorizationUrl}${paramDelimiter}client_id=${clientId}&scope=${getScopes(
      scopes
    )}&redirect_uri=${encodeURIComponent(
      OAUTH2_AUTHORIZATION_FLOW_REDIRECT_URI()
    )}&state=${redirectState}&response_type=${CODE}`;

    // Create or update the integration on screen before redirecting
    // to save field values like clientSecret etc.
    if (draftIntegration.integration_id) {
      await dispatch(
        updateIntegration({
          assistantId: draftIntegration.assistant_id,
          environmentId: draftIntegration.environment_id,
          integration: draftIntegration
        })
      ).unwrap();
    } else {
      await dispatch(
        createIntegration({
          assistantId,
          environmentId,
          integration: draftIntegration
        })
      ).unwrap();
    }

    window.location.href = buildAuthURL;
  };

  // Below useMount will initialize custom flow fields
  useMount(() => {
    if (customFlow) {
      // Create a custom flow object under schema and secrets
      updateFieldValue(
        FIELDS.OAUTH2.CUSTOM.FLOW(selectedFlowType),
        defaultOAuth2CustomFlowSchema()
      );
      updateFieldValue(
        FIELDS.OAUTH2.CUSTOM.PRIVATE_FLOW(selectedFlowType),
        defaultOAuth2CustomFlowSecretSchema()
      );
    }
  });

  // If selected flowType is one of the custom flow type x-
  // handle all the fields in the below useEffect and update those based on saved values
  useEffect(() => {
    if (customFlow) {
      updateFieldValue(
        FIELDS.OAUTH2.CUSTOM.GRANT_TYPE(selectedFlowType),
        grantType
      );
      updateFieldValue(
        FIELDS.OAUTH2.CUSTOM.REFRESH_URL(selectedFlowType),
        refreshUrl
      );
      updateFieldValue(
        FIELDS.OAUTH2.CUSTOM.TOKEN_URL(selectedFlowType),
        tokenUrl
      );
      updateFieldValue(
        FIELDS.OAUTH2.CUSTOM.AUTH_TYPE(selectedFlowType),
        clientAuthType
      );
      updateFieldValue(
        FIELDS.OAUTH2.CUSTOM.HEADER_PREFIX(selectedFlowType),
        headerPrefix
      );
      // "custom_secrets" & custom_params has a default pattern of {type: 'value', value: ''}
      // Since secretKeys & paramKeys from openAPI are dynamic, need to set the type: 'value' here
      catalogItemFlows?.[selectedFlowType]?.secretKeys?.forEach(secret => {
        updateFieldValue(
          FIELDS.OAUTH2.CUSTOM.SECRET_TYPE(selectedFlowType, secret),
          VALUE
        );
      });
      catalogItemFlows?.[selectedFlowType]?.paramKeys?.forEach(param => {
        updateFieldValue(
          FIELDS.OAUTH2.CUSTOM.PARAM_TYPE(selectedFlowType, param),
          VALUE
        );
      });
      if (!_.isEmpty(scopes)) {
        updateFieldValue(FIELDS.OAUTH2.CUSTOM.SCOPES(selectedFlowType), scopes);
      }
    }
  }, [
    catalogItemFlows,
    clientAuthType,
    customFlow,
    grantType,
    headerPrefix,
    refreshUrl,
    scopes,
    selectedFlowType,
    tokenUrl,
    updateFieldValue
  ]);

  // Fixme when store will start supporting partial updates
  // This useEffect is for non quick setup (edit flow) & non customFlow i.e for 3 other flow types,
  // for secrets, tooling need to send {type: 'value'} to store along with actual value since the default schema
  // (check schema.tsx) is casted for quicksetup flows.
  useEffect(() => {
    // Check if this is an edit flow, non custom flow, fields are dirtied & no auth code found from redirect in case of
    // Auth code flow
    if (!isQuickSetup && !customFlow && isDirty && !authCode) {
      updateFieldValue(
        FIELDS.OAUTH2.FLOWS_FLOW(selectedFlowType),
        defaultOAuth2Secrets()[selectedFlowType]
      );
    }
  }, [
    authCode,
    customFlow,
    isDirty,
    isQuickSetup,
    selectedFlowType,
    updateFieldValue
  ]);

  // If no supported flows found (as we just support 4 flows viz. password, client creds, auth code & custom flow starts with x-)
  // Show inline warning notification
  if (_.isEmpty(flows)) {
    return (
      <InlineNotification
        kind="warning"
        statusIconDescription="notification"
        subtitle={t(messages.noFlowsFoundDescription)}
        title={t(messages.noFlowsFoundTitle)}
      />
    );
  }

  return (
    <>
      <div className="TabContent__field-container">
        <Dropdown
          id={t(messages.grantType)}
          label={getGrantFriendlyName[flows[selectedFlowType]] || ''}
          titleText={t(messages.grantType)}
          items={flowsDropdownItems}
          disabled={readOnly}
          selectedItem={flowsDropdownItems?.find(
            item => item.value === selectedFlowType
          )}
          itemToString={item => item.text}
          onChange={(val): void => {
            updateFieldValue(
              FIELDS.OAUTH2.PREFERRED_FLOW,
              val.selectedItem.value
            );
            trigger();
          }}
        />
      </div>
      {OAUTH2_FLOWS_REQUIRED_FIELDS[selectedFlowType]?.map((field, index) => {
        // fieldSchema is used to populate oauth secrets schema
        const fieldSchema =
          FIELDS.OAUTH2[field.toUpperCase()](selectedFlowType);
        if (field === USERNAME) {
          return (
            <div className="TabContent__field-container" key={field}>
              <TextInput
                {...registerInputField({
                  field: fieldSchema,
                  isQuickSetup,
                  labelText: messages.username,
                  placeholderText: messages.username,
                  changeState: oauth2FieldChangeStates[index],
                  readOnly
                })}
              />
            </div>
          );
        } else {
          const fieldName = _.camelCase(field);
          // For the better UX, this hides the client secret field for authcode flow, till it gets back "auth code" from the auth redirect
          if (
            selectedFlowType === OAUTH2_FLOW_TYPES.AUTHORIZATION_CODE &&
            !authCode &&
            field === CLIENT_SECRET
          ) {
            return null;
          }
          return (
            <div className="TabContent__field-container" key={field}>
              <PasswordInput
                {...registerInputField({
                  field: fieldSchema,
                  isPasswordField: true,
                  // If authcode found, it still is in quicksetup
                  isQuickSetup: authCode ? true : isQuickSetup,
                  placeholderText: messages[`${fieldName}Placeholder`],
                  changeState: oauth2FieldChangeStates[index],
                  readOnly
                })}
                labelText={
                  messages[`${fieldName}Tooltip`] ? (
                    <Tooltip
                      triggerText={t(messages[fieldName])}
                      direction="right">
                      <p>{t(messages[`${fieldName}Tooltip`])}</p>
                    </Tooltip>
                  ) : (
                    t(messages[fieldName])
                  )
                }
              />
            </div>
          );
        }
      })}
      {
        /* This shows the redirect uri section, copy to clipboard & grant/regrant access button for auth code flow */
        selectedFlowType === OAUTH2_FLOW_TYPES.AUTHORIZATION_CODE && (
          <div className="TabContent__field-container">
            <div className="TabContent__field-inline-notification">
              {t(messages.copyToClipBoard)}
            </div>
            <div className="TabContent__field-copy-container">
              <CodeSnippet type="single">
                {OAUTH2_AUTHORIZATION_FLOW_REDIRECT_URI()}
              </CodeSnippet>
            </div>
            <Button
              onClick={handleAuthorizationCodeFlow}
              disabled={!authorizationUrl || !clientId}>
              {authorizationCode
                ? t(messages.reGrantAccess)
                : t(messages.grantAccess)}
            </Button>
          </div>
        )
      }
      {customFlow && (
        // Render all the fields associated with custom flow - custom secrets & custom params
        <>
          {catalogItemFlows?.[selectedFlowType]?.secretKeys?.length > 0 && (
            <div className="TabContent__field-container">
              <div className="TabContent__subtitle">
                {t(messages.customSecretsHeader)}
              </div>
              {catalogItemFlows?.[selectedFlowType]?.secretKeys?.map(
                (secret, index) => {
                  const secretValue = getFieldValue(
                    FIELDS.OAUTH2.CUSTOM.SECRET_VALUE(selectedFlowType, secret)
                  );
                  return (
                    <div
                      className="TabContent__field-container"
                      key={`${secret}_${selectedFlowType}_${index}`}>
                      <PasswordInput
                        {...registerInputField({
                          field: FIELDS.OAUTH2.CUSTOM.SECRET_VALUE(
                            selectedFlowType,
                            secret
                          ),
                          isPasswordField: true,
                          // quicksetup is true for all the custom flow seret fields to adjust the password fields design
                          isQuickSetup: true,
                          placeholderText: messages.customSecretsPlaceholder,
                          readOnly
                        })}
                        labelText={t(messages.customSecret, {
                          secretName: _.capitalize(secret)
                        })}
                        invalid={!isQuickSetup && !secretValue && !readOnly}
                        invalidText={t(messages.reEnterSecret)}
                        readOnly={readOnly}
                      />
                    </div>
                  );
                }
              )}
            </div>
          )}
          <>
            {catalogItemFlows?.[selectedFlowType]?.paramKeys?.length > 0 && (
              <div className="TabContent__field-container">
                <div className="TabContent__subtitle">
                  {t(messages.customParamHeader)}
                </div>
                {catalogItemFlows?.[selectedFlowType]?.paramKeys?.map(param => (
                  <div
                    className="TabContent__field-container"
                    key={`${param}_${selectedFlowType}`}>
                    <TextInput
                      {...registerInputField({
                        field: FIELDS.OAUTH2.CUSTOM.PARAM_VALUE(
                          selectedFlowType,
                          param
                        ),
                        isQuickSetup,
                        placeholderText: messages.customParamPlaceholder,
                        readOnly
                      })}
                      labelText={t(messages.customParam, {
                        customParam: _.capitalize(param)
                      })}
                      readOnly={readOnly}
                    />
                  </div>
                ))}
              </div>
            )}
          </>
        </>
      )}
      <div className="TabContent__field-container">
        <Dropdown
          id={t(messages.clientAuthType)}
          label={clientAuthType}
          titleText={
            <Tooltip triggerText={t(messages.clientAuthType)} direction="right">
              <p>{t(messages.clientAuthTypeTooltip)}</p>
            </Tooltip>
          }
          items={clientAuthTypeDropdownItems}
          disabled={readOnly}
          selectedItem={clientAuthTypeDropdownItems?.find(
            item => item.value === clientAuthType
          )}
          itemToString={item => item.text}
          onChange={val => {
            updateFieldValue(
              customFlow
                ? FIELDS.OAUTH2.CUSTOM.AUTH_TYPE(selectedFlowType)
                : FIELDS.OAUTH2.CLIENT_AUTH_TYPE(selectedFlowType),
              val.selectedItem.value
            );
            trigger();
          }}
        />
      </div>
      <div className="TabContent__field-container">
        <TextInput
          {...registerInputField({
            field: customFlow
              ? FIELDS.OAUTH2.CUSTOM.HEADER_PREFIX(selectedFlowType)
              : FIELDS.OAUTH2.HEADER_PREFIX(selectedFlowType),
            isQuickSetup,
            placeholderText: messages.headerPrefixPlaceholder,
            changeState: headerPrefixChangeState,
            readOnly
          })}
          labelText={
            <Tooltip triggerText={t(messages.headerPrefix)} direction="right">
              <p>{t(messages.headerPrefixTooltip)}</p>
            </Tooltip>
          }
        />
      </div>
    </>
  );
};
